import React from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { ErrorBoundary, SectionErrorFallback } from '@modernloop/shared/helper-components';
import { useFlag } from '@modernloop/shared/feature-flag';
import { Box, Grid, Stack } from '@mui/material';

import { Routes } from 'src/constants/routes';

import AgentChatDock from 'src/views-new/AIAssistant/AgentChatDock';
import { AgentChatSessionProvider } from 'src/views-new/AIAssistant/AgentChatSessionProvider';
import OmniSearch from 'src/views-new/OmniSearch';
import OmniSearchProvider from 'src/views-new/OmniSearch/OmniSearchProvider';
import SidePanel from 'src/views-new/sidepanel';

import AppNav from './components/AppNav';
import Banners from './components/banners';

const DashboardLayout: React.FC<{ children?: React.ReactNode }> = ({ children }): JSX.Element => {
  const globalAgentEnabled = useFlag('AI_GLOBAL_AGENT');
  const { pathname } = useLocation();
  const agentRouteMatch = matchPath<{ threadId?: string }>(pathname, {
    exact: true,
    path: [Routes.AgentThread, Routes.Agent],
  });
  const isAgentFullPageRoute = Boolean(agentRouteMatch);
  const routeThreadId = agentRouteMatch ? (agentRouteMatch.params.threadId ?? null) : undefined;

  return (
    <OmniSearchProvider>
      <AgentChatSessionProvider
        isAgentEnabled={globalAgentEnabled}
        isFullPageRoute={isAgentFullPageRoute}
        routeThreadId={routeThreadId}
      >
        <Stack
          sx={{
            height: '100%',
            width: '100%',
            overflow: 'hidden',
            backgroundColor: (theme) => theme.palette.background.default,
          }}
          flexWrap="nowrap"
          direction="row"
        >
          <Box
            sx={{
              flexShrink: 0,
              flexWrap: 'nowrap',
              height: '100%',
              overflowX: 'hidden',
            }}
          >
            <AppNav />
          </Box>

          <Grid
            spacing={0}
            container
            wrap="nowrap"
            direction="column"
            sx={{
              boxShadow: (theme) => theme.shadows[3],
              flexGrow: 1,
              overflowX: 'hidden',
              overflowY: 'auto',
              backgroundColor: (theme) => theme.palette.background.paper,
              width: '100%',
              height: '100%',
            }}
          >
            <Banners />
            <Stack sx={{ height: '100%', width: '100%' }} justifyContent="center">
              {children}
            </Stack>
          </Grid>
        </Stack>
        <AgentChatDock />
        <ErrorBoundary fallback={<SectionErrorFallback />} scope="side-panel">
          <SidePanel />
        </ErrorBoundary>
        <ErrorBoundary fallback={<></>} scope="omni-search">
          <OmniSearch />
        </ErrorBoundary>
      </AgentChatSessionProvider>
    </OmniSearchProvider>
  );
};

export default DashboardLayout;
